'use client'
import { observer } from 'mobx-react-lite'
import dynamic from 'next/dynamic'
import { FunctionComponent, PropsWithChildren, useLayoutEffect } from 'react'
import { ToastContainer } from 'react-toastify'

import { cookiesPopupStore } from '@/store/CookiesPopup.store'
import { orderCallPopupStore } from '@/store/OrderCallPopup.store'

import { Message } from '@/components/Message'
import { OrderCall } from '@/modules/OrderCall'
import { SiteOptionsData } from '@/services/Api/generated'

import { NEW_YEAR_THEME, SITE_URL } from '@/config'

import { Footer } from './components/Footer'
import { Header } from './components/Header'

import { AcceptPopup } from '@/components/AcceptPopup'
import { ConfirmationPopup } from '@/components/ConfirmationPopup'
import { MaxPopup } from '@/components/MaxPopup'
import { PopupAuth } from '@/shared/Auth/modules/PopupAuth'
import { navigationMenuStore } from '@/store/NavigationMenu'
import { POPUP_TYPE, popupStore } from '@/store/Popup'
import { popupAuthStore } from '@/store/PopupAuth.store'
import styles from './MainLayout.module.scss'

const Snow = dynamic(
  () => import('@/modules/NewYearElements/Snow').then((m) => m.Snow),
  { ssr: false }
)

const CookiesPopup = dynamic(
  () => import('./components/CookiesPopup').then((m) => m.CookiesPopup),
  { ssr: false }
)

type MainLayoutProps = {
  data: SiteOptionsData
}
export const MainLayout: FunctionComponent<PropsWithChildren<MainLayoutProps>> =
  observer(({ children, data }) => {
    useLayoutEffect(() => {
      cookiesPopupStore.init()
      navigationMenuStore.getServicesList()
    }, [])

    return (
      <>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'SiteNavigationElement',
              about: {
                '@type': 'ItemList',
                itemListElement: [
                  {
                    '@type': 'ItemList',
                    url: `${SITE_URL}/`,
                    name: 'Главная',
                  },
                  {
                    '@type': 'ItemList',
                    url: `${SITE_URL}/mktu`,
                    name: 'МКТУ',
                  },
                  {
                    '@type': 'ItemList',
                    url: `${SITE_URL}/prices`,
                    name: 'Цены',
                  },
                  {
                    '@type': 'ItemList',
                    url: `${SITE_URL}/calculator`,
                    name: 'Калькулятор',
                  },
                  {
                    '@type': 'ItemList',
                    url: `${SITE_URL}/o-kompanii`,
                    name: 'О нас',
                  },
                  {
                    '@type': 'ItemList',
                    url: `${SITE_URL}/blog`,
                    name: 'Блог',
                  },
                  {
                    '@type': 'ItemList',
                    url: `${SITE_URL}/contacts`,
                    name: 'Контакты',
                  },
                  {
                    '@type': 'ItemList',
                    url: `${SITE_URL}/documents`,
                    name: 'Документы',
                  },
                  {
                    '@type': 'ItemList',
                    url: `${SITE_URL}/sitemap`,
                    name: 'Карта сайта',
                  },
                ],
              },
            }),
          }}
        ></script>
        {NEW_YEAR_THEME ? <Snow /> : null}
        <div className={styles.container}>
          <Header
            telegram={data.telegram}
            whatsapp={data.whatsapp}
            max={data.max}
          />

          <main className={styles.content}>{children}</main>

          <div className={styles.footer}>
            <div className={styles.footerContent}>
              <Footer
                phone={data.phone}
                telegram={data.telegram}
                whatsapp={data.whatsapp}
                max={data.max}
              />
            </div>
          </div>

          <Message className={styles.message} />

          {orderCallPopupStore.visible ? (
            <OrderCall
              helpType={orderCallPopupStore.helpType}
              title={orderCallPopupStore.title}
            />
          ) : null}

          {cookiesPopupStore.isVisible ? (
            <CookiesPopup onHide={cookiesPopupStore.hidePopup} />
          ) : null}

          {popupAuthStore.isVisible ? (
            <PopupAuth onClose={popupAuthStore.hide} />
          ) : null}

          {popupStore.activated === POPUP_TYPE.ACCEPT_POPUP &&
          popupStore.data != null ? (
            <AcceptPopup
              onClose={popupStore.hidePopup}
              data={popupStore.data}
            />
          ) : null}

          {popupStore.activated === POPUP_TYPE.CONFIRM_POPUP &&
          popupStore.data != null ? (
            <ConfirmationPopup
              onClose={popupStore.hidePopup}
              data={popupStore.data}
            />
          ) : null}

          <MaxPopup />

          <ToastContainer
            position="bottom-right"
            theme="dark"
            style={{ zIndex: 100001 }}
          />
        </div>
      </>
    )
  })
